// Shared Leanpay popup module (reusable across widgets)
(function (window) {
  if (window.LeanpayPopup) {
    return;
  }

  const DEFAULT_CURRENCY = "EUR";
  const DEFAULT_LOCALE = "sl-SI";
  const CURRENCY_DECIMAL_PLACES = 2;

  const SELECTORS = {
    widgetWrapper: ".lp-widget-wrapper",
    openPopup: ".lp-open-popup",
    checkLimit: ".lp-check-limit",
    popupOverlay: ".lp-popup-overlay",
    popupContent: ".lp-popup-content",
    popupMonthsText: ".lp-popup-months-text",
    popupPriceText: ".lp-popup-price-text",
  };

  let DYNAMIC_MONTH_OPTIONS = [];
  let popup = null;

  function normalizeLocale(locale) {
    if (!locale || typeof locale !== "string") {
      return DEFAULT_LOCALE;
    }

    const localeMap = {
      en: "en-US",
      sl: "sl-SI",
      hr: "hr-HR",
      ro: "ro-RO",
      de: "de-DE",
      fr: "fr-FR",
      it: "it-IT",
      es: "es-ES",
    };

    if (
      locale.includes("Drop") ||
      locale.includes("Shop") ||
      locale.length > 10
    ) {
      return DEFAULT_LOCALE;
    }

    const shortCode = locale.toLowerCase().split("-")[0];
    if (localeMap[shortCode]) {
      return localeMap[shortCode];
    }

    try {
      new Intl.NumberFormat(locale);
      return locale;
    } catch (e) {
      return DEFAULT_LOCALE;
    }
  }

  function formatCurrency(
    amount,
    currency = DEFAULT_CURRENCY,
    locale = DEFAULT_LOCALE,
  ) {
    const normalizedLocale = normalizeLocale(locale);
    try {
      const formattedNumber = new Intl.NumberFormat(normalizedLocale, {
        style: "decimal",
        minimumFractionDigits: CURRENCY_DECIMAL_PLACES,
        maximumFractionDigits: CURRENCY_DECIMAL_PLACES,
      }).format(amount);
      return `${formattedNumber} ${currency}`;
    } catch (e) {
      try {
        const formattedNumber = new Intl.NumberFormat(DEFAULT_LOCALE, {
          style: "decimal",
          minimumFractionDigits: CURRENCY_DECIMAL_PLACES,
          maximumFractionDigits: CURRENCY_DECIMAL_PLACES,
        }).format(amount);
        return `${formattedNumber} ${currency}`;
      } catch (_) {
        return `${amount.toFixed(CURRENCY_DECIMAL_PLACES)} ${currency}`;
      }
    }
  }

  function extractAvailableMonthOptions(labeledPlans) {
    if (
      !labeledPlans ||
      !Array.isArray(labeledPlans) ||
      labeledPlans.length === 0
    ) {
      return [];
    }
    return [...new Set(labeledPlans.map((plan) => plan.numberOfMonths))]
      .filter((months) => months && months > 0)
      .sort((a, b) => a - b);
  }

  function generateSliderLabelsHtml(monthOptions) {
    return monthOptions
      .map((months) => `<span>${months}</span>`)
      .join("\n              ");
  }

  function getTextCenterPosition(labelElement) {
    const labelRect = labelElement.getBoundingClientRect();
    return labelRect.left + labelRect.width / 2;
  }

  function calculateNaturalThumbPosition(
    sliderElement,
    currentIndex,
    totalOptions,
  ) {
    const sliderRect = sliderElement.getBoundingClientRect();
    const sliderWidth = sliderRect.width;
    const thumbRadius = 21.45 / 2;
    const effectiveTrackWidth = sliderWidth - thumbRadius * 2;
    const trackStart = sliderRect.left + thumbRadius;
    const percentage =
      totalOptions > 1 ? (currentIndex / (totalOptions - 1)) * 100 : 0;
    return trackStart + (effectiveTrackWidth * percentage) / 100;
  }

  function calculateSliderPosition(
    labelsContainer,
    sliderElement,
    currentIndex,
    totalOptions,
  ) {
    if (totalOptions <= 1) {
      return { offsetPx: 0, percentage: 0, fillWidth: 0 };
    }
    const labels = labelsContainer.querySelectorAll("span");
    if (labels.length !== totalOptions || !labels[currentIndex]) {
      return { offsetPx: 0, percentage: 0, fillWidth: 0 };
    }
    const naturalThumbPosition = calculateNaturalThumbPosition(
      sliderElement,
      currentIndex,
      totalOptions,
    );
    const textCenterPosition = getTextCenterPosition(labels[currentIndex]);
    const offsetPx = textCenterPosition - naturalThumbPosition;

    const sliderRect = sliderElement.getBoundingClientRect();
    const finalThumbPosition = naturalThumbPosition + offsetPx;
    const fillWidth = Math.min(
      finalThumbPosition - sliderRect.left,
      sliderRect.width,
    );
    const naturalPercentage = (currentIndex / (totalOptions - 1)) * 100;

    return {
      offsetPx: Math.round(offsetPx * 100) / 100,
      percentage: naturalPercentage,
      fillWidth: Math.max(0, Math.round(fillWidth * 100) / 100),
    };
  }

  function isMobileDevice() {
    return (
      Math.min(window.screen.width, window.screen.height) < 768 ||
      navigator.userAgent.indexOf("Mobi") > -1
    );
  }

  function handleOutsideClick(e) {
    if (
      popup &&
      !e.target.closest(SELECTORS.popupContent) &&
      !e.target.closest(SELECTORS.openPopup) &&
      !e.target.closest(SELECTORS.checkLimit)
    ) {
      closePopup();
    }
  }

  function closePopup() {
    const allPopups = document.querySelectorAll(SELECTORS.popupOverlay);
    allPopups.forEach((p) => p.classList.remove("show"));

    if (popup) {
      const slider = popup.querySelector(".lp-popup-slider");
      if (slider && slider._resizeHandler) {
        window.removeEventListener("resize", slider._resizeHandler);
        slider._resizeHandler = null;
      }
    }

    const existingStyle = document.getElementById("lp-dynamic-positioning");
    if (existingStyle) {
      existingStyle.remove();
    }

    popup = null;
    document.documentElement.style.overflow = "";
    document.removeEventListener("click", handleOutsideClick);
  }

  function updatePopupContent(
    plansData,
    currency = DEFAULT_CURRENCY,
    locale = DEFAULT_LOCALE,
  ) {
    const monthsText = popup.querySelector(SELECTORS.popupMonthsText);
    const priceText = popup.querySelector(SELECTORS.popupPriceText);
    const slider = popup.querySelector(".lp-popup-slider");
    const sliderLabelsContainer = popup.querySelector(
      ".lp-popup-slider-labels",
    );

    if (!monthsText || !priceText || !slider || !sliderLabelsContainer) {
      return;
    }

    const options = plansData.labeledPlans || [];
    if (options.length === 0) {
      return;
    }

    DYNAMIC_MONTH_OPTIONS = extractAvailableMonthOptions(options);
    slider.setAttribute("max", DYNAMIC_MONTH_OPTIONS.length - 1);
    sliderLabelsContainer.innerHTML = generateSliderLabelsHtml(
      DYNAMIC_MONTH_OPTIONS,
    );

    const sliderLabels = popup.querySelectorAll(".lp-popup-slider-labels span");

    function getBestAvailablePlan(labeledPlans, targetMonths) {
      const exactMatch = labeledPlans.find(
        (plan) => plan.numberOfMonths === targetMonths,
      );
      if (exactMatch) return exactMatch;
      const availableMonths = labeledPlans
        .map((p) => p.numberOfMonths)
        .sort((a, b) => a - b);
      const closest = availableMonths.reduce((prev, curr) =>
        Math.abs(curr - targetMonths) < Math.abs(prev - targetMonths)
          ? curr
          : prev,
      );
      return labeledPlans.find((p) => p.numberOfMonths === closest) || null;
    }

    function adjustThumbMargin(sliderElement, index) {
      const labelsContainer = popup.querySelector(".lp-popup-slider-labels");
      if (!labelsContainer) return;
      requestAnimationFrame(() => {
        const totalOptions = DYNAMIC_MONTH_OPTIONS.length;
        const positionData = calculateSliderPosition(
          labelsContainer,
          sliderElement,
          index,
          totalOptions,
        );
        const thumbOffsetValue = Math.round(positionData.offsetPx * 100) / 100;
        sliderElement.style.setProperty(
          "--slider-fill-width",
          `${positionData.fillWidth}px`,
        );
        const popupId = popup.id;
        const styles = `
        #${popupId} .lp-popup-slider::-webkit-slider-thumb { margin-left: ${thumbOffsetValue}px; }
        #${popupId} .lp-popup-slider::-moz-range-thumb { margin-left: ${thumbOffsetValue}px; }
      `;
        const styleElementId = `lp-dynamic-positioning-${popupId}`;
        const existingStyle = document.getElementById(styleElementId);
        if (existingStyle) existingStyle.remove();
        const styleElement = document.createElement("style");
        styleElement.id = styleElementId;
        styleElement.textContent = styles;
        document.head.appendChild(styleElement);
      });
    }

    function updatePopupDisplay(monthIndex, sliderElement = slider) {
      const targetMonths = DYNAMIC_MONTH_OPTIONS[monthIndex];
      const plan = getBestAvailablePlan(options, targetMonths);
      adjustThumbMargin(sliderElement, monthIndex);
      if (plan) {
        const monthsLabel =
          window.leanpayTranslations?.installment_widget?.months || "months";
        monthsText.textContent = `${targetMonths} ${monthsLabel}`;
        const perMonthText =
          window.leanpayTranslations?.installment_widget?.per_month ||
          "/ month";
        priceText.textContent = `${formatCurrency(plan.installmentAmount, currency, locale)} ${perMonthText}`;
        sliderLabels.forEach((label, index) => {
          if (index === monthIndex) label.classList.add("active");
          else label.classList.remove("active");
        });
      }
    }

    slider.value = 0;
    const newSlider = slider.cloneNode(true);
    slider.parentNode.replaceChild(newSlider, slider);
    setTimeout(() => {
      updatePopupDisplay(0, newSlider);
    }, 10);

    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const currentIndex = parseInt(newSlider.value, 10);
        adjustThumbMargin(newSlider, currentIndex);
      }, 100);
    };
    window.addEventListener("resize", handleResize);
    newSlider._resizeHandler = handleResize;

    const sliderLabelsAfter = popup.querySelectorAll(
      ".lp-popup-slider-labels span",
    );
    sliderLabelsAfter.forEach((label, index) => {
      label.addEventListener("click", () => {
        newSlider.value = index;
        updatePopupDisplay(index, newSlider);
      });
    });

    newSlider.addEventListener("input", () => {
      const monthIndex = parseInt(newSlider.value, 10);
      newSlider.value = monthIndex;
      updatePopupDisplay(monthIndex, newSlider);
    });

    newSlider.addEventListener("change", () => {
      const monthIndex = parseInt(newSlider.value, 10);
      newSlider.value = monthIndex;
      updatePopupDisplay(monthIndex, newSlider);
    });
  }

  function createPopupElement(trigger, infoUrl = null) {
    const widgetWrapper = trigger.closest(SELECTORS.widgetWrapper);
    if (!widgetWrapper) {
      return null;
    }
    const widgetId =
      widgetWrapper.dataset.lpWidgetId ||
      `lp-widget-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    widgetWrapper.dataset.lpWidgetId = widgetId;
    const popupId = `lp-popup-${widgetId}`;
    const existingPopup = document.getElementById(popupId);
    if (existingPopup) {
      return existingPopup;
    }

    const logoUrl =
      widgetWrapper.getAttribute("data-logo-url") || "/files/leanpay-logo.png";
    const translations = window.leanpayTranslations?.installment_widget || {
      calculate_installments: "Calculate installments",
      close_popup: "Close popup",
      months: "months",
      per_month: "/ month",
      info_text:
        "Leanpay enables quick and easy installment payments online. For installment payment, select Leanpay at checkout.",
      check_limit: "Check your limit",
      more_info: "More information",
    };

    const moreInfoLink = infoUrl
      ? `<a href="${infoUrl}" target="_blank" rel="noopener noreferrer">${translations.more_info}</a>`
      : "";

    const popupHtml = `
    <div class="lp-popup-overlay" id="${popupId}">
      <div class="lp-popup-content">
        <div class="lp-popup-close-row" id="lp-popup-close-row-${widgetId}">
          <span>${translations.calculate_installments}</span>
          <button class="lp-popup-close-button" id="lp-popup-close-button-${widgetId}" aria-label="${translations.close_popup}">&times;</button>
        </div>
        <div class="lp-popup-divider" id="lp-popup-divider-${widgetId}"></div>

        <img src="${logoUrl}" class="lp-popup-logo" alt="Leanpay logo" width="80" height="20" />

        <div class="lp-popup-main-display">
          <div class="lp-popup-month-slider-container">
            <div class="lp-popup-slider-labels">
              ${generateSliderLabelsHtml(DYNAMIC_MONTH_OPTIONS)}
            </div>
            <input type="range" class="lp-popup-slider" id="lp-popup-slider-${widgetId}" min="0" max="${DYNAMIC_MONTH_OPTIONS.length - 1}" value="0" step="1" />
          </div>

          <div class="lp-popup-month-price-display">
            <span class="lp-popup-months-text">${DYNAMIC_MONTH_OPTIONS[0] || 0} ${translations.months}</span>
            <span class="lp-popup-price-text">0.00 EUR ${translations.per_month}</span>
          </div>
        </div>

        <div class="lp-popup-info">
          ${translations.info_text}
        </div>

        <div class="lp-popup-footer">
          <a href="/apps/leanpay-installments/prequalify">${translations.check_limit}</a>
          ${moreInfoLink}
        </div>
      </div>
    </div>
  `;

    try {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = popupHtml;
      const popupElement = tempDiv.firstElementChild;
      if (!popupElement) {
        return null;
      }
      widgetWrapper.appendChild(popupElement);
      return popupElement;
    } catch (error) {
      return null;
    }
  }

  function createPopup(
    trigger,
    plansData,
    productPrice,
    currency = DEFAULT_CURRENCY,
    locale = DEFAULT_LOCALE,
  ) {
    if (!trigger) {
      return;
    }
    closePopup();
    popup = createPopupElement(trigger, plansData.infoUrl);
    if (!popup) {
      return;
    }

    const isMobile = isMobileDevice();
    const closeRow = popup.querySelector(".lp-popup-close-row");
    const divider = popup.querySelector(".lp-popup-divider");
    const closeBtn = popup.querySelector(".lp-popup-close-button");

    if (isMobile) {
      if (closeRow) closeRow.style.display = "flex";
      if (divider) divider.style.display = "block";
      document.documentElement.style.overflow = "hidden";
    } else {
      if (closeRow) closeRow.style.display = "none";
      if (divider) divider.style.display = "none";
    }

    updatePopupContent(plansData, currency, locale);
    popup.classList.add("show");
    setTimeout(
      () => document.addEventListener("click", handleOutsideClick),
      10,
    );
    if (closeBtn) {
      closeBtn.addEventListener("click", closePopup);
    }
  }

  window.LeanpayPopup = {
    createPopup,
    closeAll: closePopup,
  };
})(window);